// Atoms pertaining to component atoms
import { atom } from 'jotai/vanilla';
import { IAnswer, IAnswerType, IComponent, IConditional } from '../interfaces';
import { json_parser } from './helpers';
import actions, { ActionTypes } from './actions';
import answers from './answers';
import application from './application';

const parser = atom(null, (get, set, component: IComponent) => {
  const savedAnswers: IAnswer[] = get(answers.data);
  const savedAnswer = savedAnswers.find((a) => a.componentID === component.id);

  const answer =
    json_parser(component.answer as string) ?? savedAnswer?.answer ?? savedAnswer?.availability;
  const value = json_parser(component.value as string);
  const default_value = json_parser(component.default_value as string);
  const options = component?.options ?? savedAnswer?.options;
  const action_data =
    component?.action && actions[component.action]
      ? get<Partial<IComponent>>(actions[component.action].data)
      : {};

  if (value && component.action === ActionTypes.country) {
    set(application.country, parseInt(value));
  }

  const props = { ...component, ...action_data, answer, value, default_value, options };
  // Convert IDs to strings
  if (props.conditionals !== undefined && Array.isArray(props.conditionals))
    props.conditionals = props.conditionals.map((conditional) => {
      if (conditional.component_id === null || conditional.component_id === undefined)
        return conditional;
      return {
        ...conditional,
        value: conditional.value.toString(),
      };
    });
  if (props.options !== undefined && Array.isArray(props.options))
    props.options = props.options.map((option) => ({
      ...option,
      id: option.id.toString(),
    }));
  if (props.answer !== undefined) {
    if (Array.isArray(props.answer))
      props.answer = props.answer.map((answer) =>
        typeof answer === 'number' ? answer.toString() : answer.id?.toString() ?? answer,
      );
    else if (typeof props.answer === 'number') props.answer = props.answer.toString();
  }
  if (props.value !== undefined) {
    if (Array.isArray(props.value))
      props.value = props.value.map((value) =>
        typeof value === 'number' ? value.toString() : value.id.toString(),
      );
    else if (typeof props.value === 'number') props.value = props.value.toString();
  }
  if (props.type !== 'text') {
    if (value && !Array.isArray(value)) {
      if (props.options && props.options.length > 0) {
        set(answers.add, {
          componentID: props.id,
          answer: props.options.find((option) => option.id === value.id || option.id === value),
        });
      } else set(answers.add, { componentID: props.id, answer: value });
    }
    if (default_value && props.options && !props.answer) {
      props.default_value = props.options.find((o) => o.id === default_value.toString());
      if (props.type !== 'singleselect') {
        set(answers.add, {
          componentID: props.id,
          options: [props.default_value],
        });
      } else {
        set(answers.add, {
          componentID: props.id,
          answer: props.default_value.id,
        });
      }
    }
    if (props.answer && props.options) {
      if (Array.isArray(props.answer)) {
        props.answer = props.answer.reduce((acc, id) => {
          if (props.options === undefined) return acc;
          const option = props.options.find((o) => o.id === id);
          if (option) return [...acc, option];
          return acc;
        }, []);
      }
    }
  }
  return props;
});
const list_primitive = atom<IComponent[]>([]);
// Component list
const list = atom(
  (get) => {
    const components = get(list_primitive);
    const answer_list = get<IAnswer[]>(answers.data);

    const should_show = (conditionals?: IConditional[]) => {
      if (!conditionals) return true;

      return conditionals.every(({ component_id, value }) => {
        if (!component_id) return true;
        const found_answer = answer_list.find((a: IAnswer) => a.componentID === component_id);
        if (!found_answer) return false;

        if (found_answer.answer === value) return true;

        const values = value.toString().split(',');
        return found_answer.options?.some(({ id }) => values.includes(id.toString()));
      });
    };

    const resolve_value = (component_defaults: IComponent, action_data: Partial<IComponent>) => {
      const { default_value, type, answer, options } = component_defaults;
      let value: IAnswerType | undefined = answer ?? action_data.answer ?? undefined;

      if (value === undefined && default_value) {
        value = type === 'singleselect' ? default_value : [default_value];
      }

      if (options && typeof value === 'string') {
        value = options.find((o) => o.id.toString() === value?.toString());
      }

      return value;
    };

    return components
      .map((component_defaults, position) => {
        if (!component_defaults) return null;

        const action_data =
          component_defaults.action && actions[component_defaults.action]
            ? get<Partial<IComponent>>(actions[component_defaults.action].data)
            : {};

        return {
          ...component_defaults,
          ...action_data,
          position,
          show: should_show(component_defaults.conditionals),
          value: resolve_value(component_defaults, action_data),
        };
      })
      .filter(Boolean); // Removes null values from the list
  },
  (get, set, new_list: IComponent[] | ((prev: IComponent[]) => IComponent[])) => {
    set(list_primitive, new_list);
  },
);

const set_props = atom(null, (get, set, id: string, new_state: Partial<IComponent>) => {
  set(list, (prev) => {
    const current_state = get<IComponent[]>(list).find((c) => c.id === id);
    if (current_state?.position === undefined) {
      return [...prev];
    }
    const updated_state = { ...current_state, ...new_state };
    if (updated_state.default_value) {
      if (updated_state.type !== 'singleselect') {
        updated_state.value = [updated_state.default_value];
      }
      if (updated_state.type === 'singleselect') updated_state.value = updated_state.default_value;
    }
    if (updated_state.answer) {
      updated_state.value = updated_state.answer;
    }

    // Convert answer ID to full option object for radio buttons and single selects
    if (
      updated_state.options &&
      updated_state.value &&
      (typeof updated_state.value === 'string' || typeof updated_state.value === 'number')
    ) {
      const foundOption = updated_state.options.find(
        (o) => o.id.toString() === updated_state.value?.toString(),
      );

      updated_state.value = foundOption;
    }

    const on_set_atom =
      updated_state.action && actions[updated_state.action]
        ? actions[updated_state.action].on_set
        : atom(null, () => ({}));
    const on_set_result: IComponent = set(on_set_atom, updated_state);
    prev[current_state.position] = { ...updated_state, ...on_set_result };
    return [...prev];
  });
});

export default {
  list,
  parser,
  set_props,
};
